import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../../utils/axiosInstance";

function KYCStatusBadge({ status }) {
  const key = String(status || "").toLowerCase();
  const styles = {
    approved: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
    pending: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",
    rejected: "bg-rose-50 text-rose-700 ring-1 ring-rose-200",
  };

  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
        styles[key] || "bg-gray-50 text-gray-700 ring-1 ring-gray-200"
      }`}
    >
      {status || "N/A"}
    </span>
  );
}

function Section({ title, children, right }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/70 dark:border-gray-700">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200/70 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
        {right ? <div>{right}</div> : null}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      <div className="text-sm text-gray-900 dark:text-gray-100 break-words">{value ?? "—"}</div>
    </div>
  );
}

export default function UserDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [activity, setActivity] = useState(null);

  const [loadingUser, setLoadingUser] = useState(true);
  const [loadingActivity, setLoadingActivity] = useState(true);

  const [errorUser, setErrorUser] = useState({ type: "", message: "" });
  const [errorActivity, setErrorActivity] = useState("");

  useEffect(() => {
    let alive = true;

    const fetchUser = async () => {
      setLoadingUser(true);
      setErrorUser({ type: "", message: "" });

      try {
        const res = await axios.get(`/api/admin/users/${id}`);
        if (!alive) return;
        setUser(res.data);
      } catch (err) {
        if (!alive) return;

        const status = err.response?.status;
        const message = err.response?.data?.message || err.message;

        console.error("❌ Erreur fetch user details:", err.response?.data || err.message);

        if (status === 401) return navigate("/login");
        if (status === 403)
          return setErrorUser({
            type: "forbidden",
            message: "Accès refusé (permissions insuffisantes).",
          });
        if (status === 404)
          return setErrorUser({ type: "notfound", message: "Utilisateur introuvable." });

        setErrorUser({ type: "error", message: message || "Erreur serveur." });
      } finally {
        if (alive) setLoadingUser(false);
      }
    };

    fetchUser();
    return () => {
      alive = false;
    };
  }, [id, navigate]);

  useEffect(() => {
    let alive = true;

    const fetchActivity = async () => {
      if (!user?.id) return;

      setLoadingActivity(true);
      setErrorActivity("");

      try {
        const res = await axios.get(`/api/admin/users/${id}/activity`);
        if (!alive) return;
        setActivity(res.data?.activity || null);
      } catch (err) {
        if (!alive) return;

        const status = err.response?.status;
        const message = err.response?.data?.message || err.message;

        console.error("❌ Erreur fetch user activity:", err.response?.data || err.message);

        if (status === 401) return navigate("/login");
        if (status === 403) return setErrorActivity("Accès refusé (permissions insuffisantes).");

        setErrorActivity(message || "Erreur lors du chargement de l'activité.");
      } finally {
        if (alive) setLoadingActivity(false);
      }
    };

    fetchActivity();
    return () => {
      alive = false;
    };
  }, [id, user?.id, navigate]);

  const fullName = useMemo(() => {
    if (!user) return "";
    return (
      `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
      `${user.profile?.firstName || ""} ${user.profile?.lastName || ""}`.trim() ||
      "N/A"
    );
  }, [user]);

  if (loadingUser) {
    return (
      <div className="pt-6 px-4 md:ml-64">
        <div className="max-w-7xl mx-auto">
          <div className="h-7 w-56 bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />
          <div className="mt-6 h-40 bg-gray-100 dark:bg-gray-700 rounded-2xl animate-pulse" />
        </div>
      </div>
    );
  }

  if (errorUser.type) {
    return (
      <div className="pt-6 px-4 md:ml-64">
        <div className="max-w-3xl mx-auto space-y-4">
          <div className="rounded-2xl border border-rose-200 dark:border-rose-900/40 bg-rose-50 dark:bg-rose-900/20 px-4 py-3 text-rose-700 dark:text-rose-200 text-sm">
            {errorUser.message}
          </div>
          <button
            onClick={() => navigate("/admin/users")}
            className="px-4 py-2 rounded-xl bg-gray-900 text-white hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 text-sm font-medium"
          >
            Back to users
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="pt-6 px-4 md:ml-64">
        <div className="max-w-3xl mx-auto text-sm text-gray-500 dark:text-gray-400">
          Utilisateur introuvable.
        </div>
      </div>
    );
  }

  const tx = activity?.transactions || [];
  const loans = activity?.loans || [];
  const commissions = activity?.referralCommissions || [];

  return (
    <div className="pt-6 px-4 md:ml-64 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white">
              {fullName}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">{user.email || "—"}</p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => navigate("/admin/users")}
              className="px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm"
            >
              Back
            </button>
          </div>
        </div>

        {/* Top grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Section
            title="Profile"
            right={<KYCStatusBadge status={user.profile?.kycStatus} />}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="User ID" value={user.id} />
              <Field label="Role" value={user.role || "—"} />
              <Field label="Phone" value={user.phoneNumber || "—"} />
              <Field
                label="Created at"
                value={user.createdAt ? new Date(user.createdAt).toLocaleString() : "—"}
              />
            </div>
          </Section>

          <Section title="Wallet">
            {user.wallet ? (
              <div className="flex items-end justify-between">
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Balance</div>
                  <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                    {user.wallet.balance}{" "}
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      {user.wallet.currency}
                    </span>
                  </div>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Wallet ID: {user.wallet.id}</div>
              </div>
            ) : (
              <div className="text-sm text-gray-500 dark:text-gray-400">No wallet found.</div>
            )}
          </Section>

          <Section title="Activity status">
            {loadingActivity ? (
              <div className="text-sm text-gray-500 dark:text-gray-400">Loading activity…</div>
            ) : errorActivity ? (
              <div className="rounded-xl border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-amber-800 dark:text-amber-200 text-sm">
                {errorActivity}
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Transactions</div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {tx.length}
                  </div>
                </div>
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Loans</div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {loans.length}
                  </div>
                </div>
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Commissions</div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {commissions.length}
                  </div>
                </div>
              </div>
            )}
          </Section>
        </div>

        {/* Transactions */}
        <Section
          title="Recent transactions"
          right={
            tx.length ? (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Showing {Math.min(10, tx.length)} of {tx.length}
              </span>
            ) : null
          }
        >
          {loadingActivity ? (
            <div className="text-sm text-gray-500 dark:text-gray-400">Loading…</div>
          ) : tx.length > 0 ? (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {tx.slice(0, 10).map((t) => (
                <div key={t.id} className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                  <div className="text-sm text-gray-900 dark:text-gray-100">
                    {t.transactionType || t.type || "Transaction"}
                    <span className="text-gray-500 dark:text-gray-400">
                      {" "}
                      • {t.amount} {t.currency}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {t.createdAt ? new Date(t.createdAt).toLocaleString() : "—"}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-gray-500 dark:text-gray-400">No transactions.</div>
          )}
        </Section>

        {/* Loans + Commissions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Section title="Loans">
            {loadingActivity ? (
              <div className="text-sm text-gray-500 dark:text-gray-400">Loading…</div>
            ) : loans.length > 0 ? (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {loans.slice(0, 10).map((l) => (
                  <div key={l.id} className="py-3 flex items-center justify-between">
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {l.amount} <span className="text-gray-500 dark:text-gray-400">{l.currency || ""}</span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{l.status || "—"}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-500 dark:text-gray-400">No loans.</div>
            )}
          </Section>

          <Section title="Commissions">
            {loadingActivity ? (
              <div className="text-sm text-gray-500 dark:text-gray-400">Loading…</div>
            ) : commissions.length > 0 ? (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {commissions.slice(0, 10).map((c) => (
                  <div key={c.id} className="py-3 flex items-center justify-between">
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {c.amount} <span className="text-gray-500 dark:text-gray-400">{c.currency || ""}</span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{c.source || "—"}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-500 dark:text-gray-400">No commissions.</div>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}